import { useContext, useEffect, useMemo } from "react";
import { useFlexLayout } from "react-table";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Box,
  DashPlaceholder,
  Flex,
  PaginationContext,
  SearchContext,
  SortContext,
  SortHeader,
  Text,
} from "dash-ui";

import DeliveryImage from "assets/images/png/Delivery.png";
import {
  ApiPluginUserForListFragment,
  useGetApiPluginUsersQuery,
} from "gql/generated.graphql";

import { StyledTable } from "./ApiPluginUsersPageTable.styles";
import { useNavigate } from "react-router-dom";
import { RoutesPath } from "flow-components/routes/routes";

const ApiPluginUsersPageTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "tables"]);
  const { state, getRefetchProps, setTotal } = useContext(PaginationContext);
  const { state: sortState } = useContext(SortContext);
  const { searchSlug } = useContext(SearchContext);
  const pagination = getRefetchProps(state);

  const { data, loading } = useGetApiPluginUsersQuery({
    variables: {
      pagination,
      ...(sortState.fieldName && { sort: [sortState] }),
      filter: {
        ...(searchSlug?.length >= 3 && { searchText: searchSlug }),
      },
    },
  });

  const { users, total } = useMemo(
    () => ({
      users: data?.getApiPluginUsers.collection || [],
      total: data?.getApiPluginUsers.totalCount || 0,
    }),
    [data]
  );

  const navigateToPluginUserDetailsPage = (id: string) =>
    navigate(RoutesPath.APIManagementPluginUserDetails.replace(":id", id));

  const navigateToCompanyDetailsPage = (id: string) =>
    navigate(RoutesPath.APIManagementCompanyDetails.replace(":id", id));

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="profile.firstName" isDefault>
            {t("common:labels.fullName")}
          </SortHeader>
        ),
        accessor: "profile.firstName",
        Cell: ({
          row,
        }: {
          row: { original: ApiPluginUserForListFragment };
        }) => {
          const { firstName, lastName } = row.original.profile;
          const apiCredentialsId = row.original.apiCredentials?._id ?? "";

          return (
            <Flex
              onClick={() => navigateToPluginUserDetailsPage(apiCredentialsId)}
              alignItems="center"
            >
              <Box mr={4}>
                {/* {avatarFile?.publicUrl ? (
                  <Image src={avatarFile.publicUrl} size={32} />
                ) : (
                  <DashPlaceholder size={32} />
                )} */}
                <DashPlaceholder size={32} />
              </Box>
              <Text>{lastName ? `${firstName} ${lastName}` : firstName}</Text>
            </Flex>
          );
        },
      },
      {
        Header: () => (
          <SortHeader accessor="account.email">
            {t("forms:labels.email")}
          </SortHeader>
        ),
        accessor: "account.email",

        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: () => (
          <SortHeader accessor="monthlyApiAccess">
            {t("forms:labels.apiMonthlyAccess")}
          </SortHeader>
        ),
        accessor: "monthlyApiAccess",

        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: () => (
          <SortHeader accessor="apiCredentials.companyName">
            {t("forms:labels.apiCompanyName")}
          </SortHeader>
        ),
        accessor: "apiCredentials.companyName",

        Cell: ({
          row,
        }: {
          row: { original: ApiPluginUserForListFragment };
        }) => {
          const associatedCompanyId =
            row.original.apiCredentials?.associatedCompany ?? "";
          const companyName = row.original.apiCredentials?.companyName;
          return (
            <Flex
              onClick={() => navigateToCompanyDetailsPage(associatedCompanyId)}
            >
              <Text>{companyName}</Text>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, t]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const labelToShow = !loading
    ? t("pages:managementPage.emptyPlaceholder") || ""
    : "";

  return (
    <StyledTable
      tableProps={{
        data: users,
        columns,
        loading,
        maxHeight: "calc(100vh - 350px)",
        stickyHeader: true,
        layouts: [useFlexLayout],
        placeholderProps: {
          image: loading ? undefined : DeliveryImage,
          label: labelToShow,
        },
      }}
      paginationProps={{
        label: t("tables:organizationUsers.paginationLabel"),
        nextButtonLabel: t("common:labels.next"),
        previousButtonLabel: t("common:labels.previous"),
      }}
    />
  );
};

export default ApiPluginUsersPageTable;
